/**
 * 대시보드용 팩 머지 헬퍼 — 서버 `src/ontology/packs/merge.ts` 의 클라이언트 대칭 구현.
 *
 * 대시보드는 자체 Vite 빌드 타깃이라 서버 모듈(`@/ontology/...`) 을 직접 import 하지 않는다.
 * 대신 동일한 머지 규칙을 여기서 재구현해 PackPicker 의 실시간 머지 미리보기를 지원한다.
 *
 * 머지 규칙 (서버와 동일):
 * - `kind / domain / relation`: `name` 기준 dedupe, **first-win** (먼저 선택된 팩의 메타 우선)
 * - `shape`: `(sourceKind, relation, targetKind)` 자연키 기준 dedupe, union
 * - `packIds`: 입력 팩 id 를 순서대로 기록
 *
 * 규격화된 그래프 RAG 원칙상 enforcement 완화 옵션은 없다.
 */
import type {
	DomainDef,
	KindDef,
	OntologyPack,
	OntologySlice,
	RelationDef,
	ShapeDef
} from "../../api/admin-client.ts"

/**
 * 팩 배열을 하나의 최종 슬라이스로 합친다.
 *
 * @param packs 합칠 팩 배열. 빈 배열이면 빈 슬라이스(packIds: [], kinds: []) 를 반환.
 * @returns 최종 `OntologySlice` — `packIds` 포함. `presetId` 는 주입하지 않음.
 */
export function mergePacks(packs: ReadonlyArray<OntologyPack>): OntologySlice {
	const kindsMap = new Map<string, KindDef>()
	const domainsMap = new Map<string, DomainDef>()
	const relationsMap = new Map<string, RelationDef>()
	const shapesMap = new Map<string, ShapeDef>()

	for (const pack of packs) {
		for (const kind of pack.slice.kinds) {
			if (!kindsMap.has(kind.name)) kindsMap.set(kind.name, kind)
		}
		for (const domain of pack.slice.domains) {
			if (!domainsMap.has(domain.name)) domainsMap.set(domain.name, domain)
		}
		for (const relation of pack.slice.relations) {
			if (!relationsMap.has(relation.name)) relationsMap.set(relation.name, relation)
		}
		for (const shape of pack.slice.shapes) {
			const key = shapeKey(shape)
			if (!shapesMap.has(key)) shapesMap.set(key, shape)
		}
	}

	return {
		packIds: packs.map((p) => p.id),
		kinds: [...kindsMap.values()],
		domains: [...domainsMap.values()],
		relations: [...relationsMap.values()],
		shapes: [...shapesMap.values()]
	}
}

/**
 * 머지 결과의 카운트만 빠르게 계산 — PackPicker 의 미리보기 패널이 선택 변화마다
 * 전체 `mergePacks` 를 돌릴 필요 없이 수치만 얻을 때 사용. 결과값은 `mergePacks` 와 동일.
 *
 * @param packs 선택된 팩 배열
 * @returns kinds/domains/relations/shapes 각각의 dedupe 후 개수
 */
export function mergePackCounts(packs: ReadonlyArray<OntologyPack>): {
	readonly kinds: number
	readonly domains: number
	readonly relations: number
	readonly shapes: number
} {
	const kindSet = new Set<string>()
	const domainSet = new Set<string>()
	const relationSet = new Set<string>()
	const shapeSet = new Set<string>()

	for (const pack of packs) {
		for (const k of pack.slice.kinds) kindSet.add(k.name)
		for (const d of pack.slice.domains) domainSet.add(d.name)
		for (const r of pack.slice.relations) relationSet.add(r.name)
		for (const s of pack.slice.shapes) shapeSet.add(shapeKey(s))
	}

	return {
		kinds: kindSet.size,
		domains: domainSet.size,
		relations: relationSet.size,
		shapes: shapeSet.size
	}
}

/** shape 자연키 — 같은 트리플(sourceKind, relation, targetKind) 은 1개만 유지. */
function shapeKey(shape: ShapeDef): string {
	return `${shape.sourceKind}|${shape.relation}|${shape.targetKind}`
}
